import type { NotificationHandle, NotificationResult, NotifyOptions } from './types';

export interface NotificationEntry {
  id: string;
  options: NotifyOptions;
}

type Listener = () => void;

/**
 * Module-level notification queue — a tiny event emitter (no external state library), so
 * `notify(...)` works from anywhere. A mounted `NotificationProvider` subscribes via
 * `useSyncExternalStore` and renders the stack.
 */
let stack: NotificationEntry[] = [];
const listeners = new Set<Listener>();
const resolvers = new Map<string, (result: NotificationResult) => void>();
let counter = 0;

function emit(): void {
  for (const listener of listeners) listener();
}

function notify(options: NotifyOptions): NotificationHandle {
  let resolveClosed!: (result: NotificationResult) => void;
  const dismissed = new Promise<NotificationResult>((resolve) => {
    resolveClosed = resolve;
  });

  counter += 1;
  const id = `notification-${String(counter)}`;
  resolvers.set(id, resolveClosed);
  stack = [...stack, { id, options }];
  emit();

  return {
    id,
    update: (patch) => {
      update(id, patch);
    },
    dismiss: (result) => {
      dismiss(id, result);
    },
    dismissed,
  };
}

/**
 * Replace part of a visible toast's options in place — a progress value, a status line, the
 * action set — so a long-running transfer is one toast that changes rather than five that
 * replace each other (HS-1441). A toast that has already left the screen is left alone.
 */
function update(id: string, patch: Partial<NotifyOptions>): void {
  if (!stack.some((item) => item.id === id)) return;
  stack = stack.map((item) =>
    item.id === id ? { id, options: { ...item.options, ...patch } } : item,
  );
  emit();
}

function dismiss(id: string, result?: Partial<NotificationResult>): void {
  const entry = stack.find((item) => item.id === id);
  if (entry == null) return;

  const full: NotificationResult = {
    action: result?.action ?? null,
    reason: result?.reason ?? 'programmatic',
  };

  stack = stack.filter((item) => item.id !== id);
  emit();

  entry.options.onClose?.();
  const resolve = resolvers.get(id);
  resolvers.delete(id);
  resolve?.(full);
}

function dismissAll(): void {
  for (const entry of [...stack]) dismiss(entry.id, { reason: 'programmatic' });
}

// subscribe / getSnapshot are passed unbound to useSyncExternalStore, so they are arrow
// properties (never rely on `this`).
export const notificationStore = {
  subscribe: (listener: Listener): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  getSnapshot: (): NotificationEntry[] => stack,
  notify,
  update,
  dismiss,
  dismissAll,
};
